import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@nanostores/react';
import { formatDistanceToNow } from 'date-fns';
import { getAll, type ChatHistoryItem } from '~/lib/persistence';
import { $isAuthenticated } from '~/lib/stores/auth';
import { $activeGeneration } from '~/lib/stores/activeGeneration';
import { findTabByChat } from '~/lib/stores/chat';
import { getStaticPreviewUrl } from '~/components/workbench/preview-url';
import { BREAKPOINTS, useIsBelow } from '~/lib/hooks/useMediaQuery';

// Six fills the grid exactly at every breakpoint (2 / 3 columns), so the last
// row is never a ragged single card.
const MAX = 6;

// Each thumbnail is a full 1280×800 live page in an iframe — on a phone six of
// them is enough memory pressure for iOS to reload the tab. One row of two.
const PHONE_MAX = 2;

/**
 * A scaled, non-interactive thumbnail of a project's cached static preview
 * (`/p/<chatId>/?embed=1` — the same snapshot shown when a chat's sandbox is
 * asleep, so no sandbox is woken). The iframe renders the site at desktop width
 * (1280) and is transform-scaled to exactly fill the card via a ResizeObserver,
 * so the thumbnail reads as the real page, not a squished mobile layout.
 */
function ThumbPreview({ chatKey, title }: { chatKey: string; title: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.2);
  // The iframe mounts only once the card nears the viewport.
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;

    if (!el || inView) {
      return undefined;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(el);

    return () => io.disconnect();
  }, [inView]);

  useEffect(() => {
    const el = ref.current;

    if (!el) {
      return undefined;
    }

    const update = () => setScale(el.clientWidth / 1280);
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} className="relative w-full aspect-[16/10] overflow-hidden border-b border-hairline bg-raised">
      {/* faint mark sits behind the iframe as a fallback if the snapshot is blank */}
      <div className="absolute inset-0 grid place-items-center">
        <img src="/dunea-mark.svg" alt="" aria-hidden className="h-8 w-8 opacity-20" />
      </div>
      {inView && (
        <iframe
          src={getStaticPreviewUrl(chatKey)}
          title={title}
          tabIndex={-1}
          scrolling="no"
          loading="lazy"
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 origin-top-left border-0"
          style={{ width: 1280, height: 800, transform: `scale(${scale})` }}
        />
      )}
    </div>
  );
}

export function RecentProjects({ emptyFallback }: { emptyFallback?: React.ReactNode }) {
  const isAuthenticated = useStore($isAuthenticated);
  const activeGen = useStore($activeGeneration);
  const navigate = useNavigate();
  const [list, setList] = useState<ChatHistoryItem[]>([]);
  // Undefined until the first read resolves. Without this the fallback flashes
  // for a frame on every load for users who do have projects.
  const [loaded, setLoaded] = useState(false);
  const isPhone = useIsBelow(BREAKPOINTS.sm);

  const load = useCallback(() => {
    getAll()
      .then((items) => items.sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp)).slice(0, MAX))
      .then(setList)
      .catch(() => {
        /* best-effort — a missing recent list just falls back to the showcase */
      })
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      load();
    }
  }, [isAuthenticated, load]);

  // Signed out, or signed in with nothing built yet: the fallback owns the slot.
  if (!isAuthenticated) {
    return <>{emptyFallback ?? null}</>;
  }

  if (list.length === 0) {
    return <>{loaded ? (emptyFallback ?? null) : null}</>;
  }

  const open = (item: ChatHistoryItem) => {
    const key = item.urlId || item.id;
    const existing = findTabByChat(key);
    navigate(existing ? existing.path : `/chat/${key}`);
  };

  return (
    <div className="relative z-10 mt-12 w-full sm:mt-14">
      <div className="mb-3.5 flex items-baseline gap-2.5 border-t border-hairline pt-5">
        <span className="ui-caption">Recent</span>
        <div className="h-px flex-1" />
        <button
          type="button"
          onClick={() => navigate('/projects')}
          className="cursor-pointer border-0 bg-transparent p-0 font-geist text-[12px] text-muted transition-colors hover:text-accent"
        >
          All projects →
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3">
        {(isPhone ? list.slice(0, PHONE_MAX) : list).map((item) => {
          const key = item.urlId || item.id;
          const isGenerating =
            !!activeGen &&
            (activeGen.id === item.id ||
              activeGen.urlId === item.id ||
              activeGen.id === key ||
              activeGen.urlId === key);

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => open(item)}
              className="group overflow-hidden rounded-[14px] border border-hairline bg-surface text-left transition-[border-color,transform] can-hover:hover:-translate-y-0.5 hover:border-subtle"
            >
              <ThumbPreview chatKey={key} title={item.description || 'Untitled project'} />
              <div className="px-3 py-2.5 sm:px-3.5 sm:py-3">
                <div className="truncate text-[12.5px] font-medium text-primary sm:text-[13.5px]">
                  {item.description || 'Untitled project'}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  {isGenerating ? (
                    <span className="inline-flex items-center gap-1.5 font-geist-mono text-[10.5px] text-warning">
                      <span className="ui-dot ui-dot-building ui-pulsing" />
                      Building
                    </span>
                  ) : (
                    <span className="font-geist-mono text-[11px] text-faint">
                      {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
